import { useEffect, useState, type ReactNode } from "react"
import { ChevronDown } from "lucide-react"
import { cn } from "../../lib/utils"
import { Message } from "./message"
import { ThinkingIndicator } from "./thinking-indicator"
import { useAutoScroll } from "./hooks/use-auto-scroll"
import type {
  AgentDescriptor,
  ChatMessage,
  ChatProgress,
  ChatStatus,
} from "./types"
import type { ToolRendererOptions } from "./tool-registry"

interface TranscriptProps {
  agent: AgentDescriptor
  messages: ChatMessage[]
  status: ChatStatus
  onPickPrompt?: (prompt: string) => void
  rendererOptions?: ToolRendererOptions
  className?: string
  progress?: ChatProgress | null
  /** Display name shown under the current user's messages. Default "You". */
  userName?: string
  /** Computes the content rendered under EACH message (e.g. a "was this
   * helpful?" control on every completed assistant reply) — called once
   * per message, in place, rather than a host bolting a single floating
   * overlay onto the latest reply only. Return `null` to render nothing
   * for a given message. */
  renderMessageFooter?: (message: ChatMessage) => ReactNode
}

/**
 * Mounts the thinking indicator with a fade/slide-in, and — crucially — keeps
 * it mounted to play a fade/slide-out before removing it, so it doesn't just
 * pop in and out. `show` drives which animation runs; the exit animation's
 * `onAnimationEnd` performs the actual unmount.
 */
function ThinkingReveal({
  show,
  progress,
}: {
  show: boolean
  progress?: ChatProgress | null
}) {
  const [mounted, setMounted] = useState(show)
  useEffect(() => {
    if (show) setMounted(true)
  }, [show])
  if (!mounted) return null
  return (
    <div
      data-slot="agent-chat-thinking"
      className={cn(
        "flex justify-start",
        show
          ? "animate-in fade-in-0 slide-in-from-bottom-1 duration-300 ease-out"
          : "animate-out fade-out-0 slide-out-to-bottom-1 duration-200 ease-in fill-mode-forwards",
      )}
      onAnimationEnd={() => {
        if (!show) setMounted(false)
      }}
    >
      <ThinkingIndicator progress={progress} />
    </div>
  )
}

function EmptyState({
  agent,
  onPickPrompt,
}: {
  agent: AgentDescriptor
  onPickPrompt?: (prompt: string) => void
}) {
  return (
    <div className="flex h-full min-h-0 flex-col items-center justify-end px-7 pb-6 text-center">
      <h2 className="font-heading text-xl font-medium">{agent.label}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{agent.greeting}</p>
      {!agent.available && (
        <p className="mt-2 inline-block rounded-full bg-foreground/5 px-2 py-0.5 text-[0.7rem] text-muted-foreground">
          preview — limited responses
        </p>
      )}
      {agent.suggestedPrompts.length > 0 && (
        <ul className="mt-4 flex flex-wrap justify-center gap-1.5">
          {agent.suggestedPrompts.map((p) => (
            <li key={p}>
              <button
                type="button"
                onClick={() => onPickPrompt?.(p)}
                className="rounded-full border border-black/10 bg-background px-3 py-1 text-xs text-foreground hover:bg-foreground/5 dark:border-white/10"
              >
                {p}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function Transcript({
  agent,
  messages,
  status,
  onPickPrompt,
  rendererOptions,
  className,
  progress,
  userName,
  renderMessageFooter,
}: TranscriptProps) {
  const { ref, atBottom, scrollToBottom } = useAutoScroll<HTMLDivElement>([
    messages,
    status,
  ])

  if (messages.length === 0) {
    return (
      <div
        ref={ref}
        data-slot="agent-chat-transcript"
        className={cn("flex h-full min-h-0 overflow-y-auto", className)}
      >
        <EmptyState agent={agent} onPickPrompt={onPickPrompt} />
      </div>
    )
  }

  return (
    // Non-scrolling relative wrapper so the jump-to-latest button can anchor to
    // it (absolute) — decoupled from the scrolling content, which is what keeps
    // it from jittering as new tokens grow the transcript.
    <div className={cn("relative flex h-full min-h-0 flex-col", className)}>
      <div
        ref={ref}
        data-slot="agent-chat-transcript"
        className="flex h-full min-h-0 flex-col overflow-y-auto"
      >
        {/* Inner content wrapper. `mt-auto` anchors short threads to
            the bottom (so a single message floats above the composer
            rather than stacking under the host's floating close
            button at the top). The `pt-14` reserves a buffer at the
            top of the SCROLLABLE content so when the user scrolls to
            the very oldest message it sits clear of the close
            button — but mid-scroll, messages pass freely behind the
            button area, which is what the user wants. */}
        {/* px-7 aligns the message text with the composer's textarea text
            (chat-shell px-3 + composer p-2 + textarea px-2 ≈ 28px). */}
        <div className="mt-auto flex flex-col gap-2 px-7 pt-14 pb-4">
          {messages.map((m) => (
            <Message
              key={m.id}
              message={m}
              rendererOptions={rendererOptions}
              userName={userName}
              agentName={agent.label}
              footer={renderMessageFooter?.(m)}
            />
          ))}
          <ThinkingReveal
            show={status === "streaming" || status === "submitting"}
            progress={progress}
          />
          {status === "error" && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              Something went wrong. Try sending again.
            </div>
          )}
        </div>
      </div>

      {/* Jump-to-latest — anchored to the wrapper (not the scroll content), and
          always mounted: we cross-fade + slide via opacity/transform so it eases
          in and out instead of popping. */}
      <button
        type="button"
        onClick={scrollToBottom}
        aria-label="Scroll to latest"
        tabIndex={atBottom ? -1 : 0}
        aria-hidden={atBottom}
        className={cn(
          "absolute bottom-3 left-1/2 z-10 flex size-8 items-center justify-center rounded-full border border-black/10 bg-background text-foreground shadow-md transition-all duration-200 ease-out hover:bg-foreground/5 dark:border-white/10",
          atBottom
            ? "pointer-events-none -translate-x-1/2 translate-y-1 opacity-0"
            : "-translate-x-1/2 translate-y-0 opacity-100",
        )}
      >
        <ChevronDown className="size-4" />
      </button>
    </div>
  )
}
